import { readFileSync } from "node:fs";
import { resolve } from "node:path";

import { NextResponse } from "next/server";

type IncomingMessage = {
  role: "user" | "assistant";
  content: string;
};

const MODEL_NAME = "openai/gpt-oss-120b";

const DIGITAL_TWIN_SYSTEM_PROMPT = `
You are the Digital Twin of Khalid Bin Kalban.
Answer professionally, with concise confidence and practical business framing.
You only answer based on the profile below. If asked for unknown details, clearly say that information is not available yet.

Profile:
- Name: Khalid Bin Kalban
- Location: Dubai, United Arab Emirates
- Current role: Manager - Artificial Intelligence at Dubai Electricity & Water Authority (DEWA), since Jan 2025
- Summary: Leads AI initiatives focused on Generative AI and prompt engineering, driving innovation and digital transformation outcomes.

Career Journey:
- DEWA (7+ years total)
  - Manager - Artificial Intelligence (Jan 2025 - Present)
  - Manager - Digital Products & Solution Management (Sep 2024 - Feb 2025)
  - Deputy Manager - Digital Products & Solution Management (Jun 2022 - Sep 2024)
  - Senior Engineer - Innovation Discovery & Partnership (Mar 2019 - Jun 2022)
- Emirates Global Aluminium (EGA) (Sep 2014 - Mar 2019)
  - Senior Engineer in Lean, Quality and Business Transformation (Jan 2019 - Mar 2019)
  - Engineer (I) in Lean, Quality and Business Transformation (Jan 2017 - Jan 2019)
  - Engineer (II) in Lean Manufacturing (Jan 2016 - Jan 2017)
  - Graduate Trainee in Lean Manufacturing (Sep 2014 - Jan 2016)

Education:
- University of California, Berkeley: Masters in Future Energy Systems & Technology (2020 - 2022)
- University of Sharjah: Bachelor's in Industrial Engineering & Engineering Management (2010 - 2014)

Skills:
- Prompt Engineering
- Generative AI
- Artificial Intelligence
- Digital Product & Solution Management
- Innovation and Business Transformation

Certifications and Honors:
- Certified ScrumMaster
- Leading Digital and AI Transformation Program
- Certificate of Completion 1 Million Prompters

When relevant, present information as short bullet points.
`.trim();

function readKeyFromParentEnv(): string | undefined {
  try {
    const envPath = resolve(process.cwd(), "../.env");
    const envContents = readFileSync(envPath, "utf-8");
    const line = envContents
      .split("\n")
      .find((entry) => entry.trim().startsWith("OPENROUTER_API_KEY="));
    return line?.split("=")[1]?.trim();
  } catch {
    return undefined;
  }
}

function getOpenRouterKey(): string | undefined {
  return process.env.OPENROUTER_API_KEY || readKeyFromParentEnv();
}

export async function POST(request: Request) {
  try {
    const body = (await request.json()) as { messages?: IncomingMessage[] };
    const incoming = body.messages?.filter(
      (message) =>
        (message.role === "user" || message.role === "assistant") &&
        typeof message.content === "string" &&
        message.content.trim().length > 0,
    );

    if (!incoming || incoming.length === 0) {
      return NextResponse.json(
        { error: "Please include at least one message." },
        { status: 400 },
      );
    }

    const apiKey = getOpenRouterKey();
    if (!apiKey) {
      return NextResponse.json(
        {
          error:
            "Missing OPENROUTER_API_KEY. Add it to website/.env.local or the parent .env file.",
        },
        { status: 500 },
      );
    }

    const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
        "HTTP-Referer": "http://localhost:3000",
        "X-Title": "Khalid Digital Twin Website",
      },
      body: JSON.stringify({
        model: MODEL_NAME,
        messages: [
          { role: "system", content: DIGITAL_TWIN_SYSTEM_PROMPT },
          ...incoming,
        ],
        temperature: 0.5,
      }),
    });

    const data = (await response.json()) as {
      choices?: Array<{ message?: { content?: string } }>;
      error?: { message?: string };
    };

    if (!response.ok) {
      const providerError = data.error?.message || "OpenRouter request failed.";
      return NextResponse.json({ error: providerError }, { status: 502 });
    }

    const reply = data.choices?.[0]?.message?.content?.trim();
    if (!reply) {
      return NextResponse.json(
        { error: "Model returned an empty response." },
        { status: 502 },
      );
    }

    return NextResponse.json({ reply });
  } catch {
    return NextResponse.json(
      { error: "Unable to process request right now." },
      { status: 500 },
    );
  }
}
